import { defineStore, Store } from "pinia";
import { Event } from "@/interfaces/Event";
import { DefaultGetParams, GetParams } from "@/interfaces/Types.definition";
import { getAllItemsAction, getItemAction } from "./common/actions";
import { CartState } from "./CartStore";
import { CartItem } from "@/interfaces/CartItem";
import { EventSession } from "@/interfaces/EventSession";
import { useQuantityDiscountStore } from "./QuantityDiscountStore";

export interface EventState {
    currentEvent: Event | null,
    events: Event[] | null,
    sessions: EventSession[] | null,
    currentSession: EventSession | null
}

export const useEventStore = defineStore("EventStore", {
    state: (): EventState => {
        return {
            currentEvent: null,
            events: [],
            sessions: [],
            currentSession: null
        }
    },
    actions: {
        async retrieveEvent(eventId: string) {
            let params: GetParams = DefaultGetParams()
            params.populate = ['organisationId']
            await getItemAction<Event, Store<'EventStore'>>(`/events/${eventId}`, params, this, 'currentEvent')
            if (this.currentEvent) {
                if (!this.currentEvent.ticketOffice) {
                    this.currentEvent['ticketOffice'] = {
                        includeTicketCommission: false,
                        includeProductCommission: false,
                        skipOrderInfo: false,
                        skipTicketInfo: false,
                        skipAddonInfo: false,
                        paymentMethods: [
                            'cash',
                            'check',
                        ],
                        seatsIoChannels: [],
                        proposeInsurance: false
                    }
                }
                this.currentEvent['_app'] = {
                    bufferStock: 0,
                    currentStock: 0
                }
                // Plan 039 : charger les regles de reduction quantitative
                // actives pour l'event, filtrees sur le canal guichet.
                // Non bloquant : si le fetch echoue, le store retombe
                // sur un tableau vide et le panier reste fonctionnel.
                useQuantityDiscountStore().fetchActiveForEvent(this.currentEvent._id)
            }
        },
        async listPublishedEvents(organisationId: string) {
            let params: GetParams = DefaultGetParams()
            params.populate = ['organisationId']
            params.filter = { organisationId, status: 'published' }
            await getAllItemsAction<Event, Store<'EventStore'>>('/events/list', params, this, 'events')
        },
        async listAllEvents(organisationId: string) {
            let params: GetParams = DefaultGetParams()
            params.populate = ['organisationId']
            params.filter = { organisationId, status: "published,closed,passed" }
            await getAllItemsAction<Event, Store<'EventStore'>>('/events/list', params, this, 'events')
        },
        async fetchEventsSession(eventId: string) {
            let params: GetParams = DefaultGetParams()
            params.filter = { eventId: eventId }
            await getAllItemsAction<Event, Store<'EventStore'>>('/events/sessions/list', params, this, 'sessions')
        },
        updateStock(cartState: CartState) {
            const quantity: number = cartState.items.reduce((acc, item: CartItem) => {
                if (item.ticketType.isProduct) {
                    return acc
                }
                return acc + item.tickets.length
            }, 0)
            if (this.currentEvent) {
                this.currentEvent._app.currentStock = Number(this.currentEvent.ticketing.stock) - (Number(this.currentEvent._app.bufferStock) + quantity)
            }
            if (this.currentSession) {
                this.currentSession._app.currentStock = Number(this.currentSession.stock) - (Number(this.currentSession._app.bufferStock) + quantity)
            }
        }
    },
    getters: {
        isRoomPlacement(): boolean {
            if (this.currentEvent && this.currentEvent.useRoomPlacement) {
                return true
            }
            return false
        },
        roomPlacementEventId(): string | null {
            const session: EventSession | null = this.currentSession
            const event: Event  = this.currentEvent
            if (session) {
                return session._id
            }
            if (event) {
                return event._id
            }
            return null
        }
    }
})
